'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'
import { io } from 'socket.io-client'
import {
  createGlobalMemoryStore,
  EMPTY_STATION_MEMORY,
  GLOBAL_MEMORY_EVENTS,
  STATION_MEMORY_EVENTS,
} from '@/lib/global-memory-store.mjs'

const GlobalMemoryContext = createContext(null)

const GLOBAL_CACHE_BY_TOPIC = {
  'global:radar': {
    domain: 'radar',
    cdnPath: '/api/radar.json',
    backendPath: '/external/radar.json',
  },
  'global:satellite': {
    domain: 'satellite',
    cdnPath: '/api/satellite.json',
    backendPath: '/external/satellite.json',
  },
  'global:lightning': {
    domain: 'lightning',
    cdnPath: '/api/lightning.json?maxAge=8100',
    backendPath: '/lightning?maxAge=8100',
  },
  'global:alerts': {
    domain: 'alerts',
    cdnPath: '/api/alerts.json',
    backendPath: '/alerts/all',
  },
}

const STATION_CACHE_BY_TOPIC = {
  'station:metadata': 'metadata',
  'station:weather': 'weather',
  'station:camera': 'metadata',
  'station:auto': null,
  'station:message': 'metadata',
  'station:presence': null,
  'station:chat': null,
  'station:alert': 'metadata',
  'station:export': null,
}

function joinUrl(baseUrl, path) {
  return new URL(path, `${baseUrl.replace(/\/$/, '')}/`).toString()
}

async function fetchJson(url, signal) {
  const response = await fetch(url, { cache: 'no-store', signal })
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`)
  return response.json()
}

function getGlobalCacheUrls(endpoint, cacheConfig) {
  const url = new URL(endpoint)
  const hostname = url.hostname.toLowerCase()
  const isBackend = hostname.includes('backend') || (
    (hostname === 'localhost' || hostname === '127.0.0.1') && url.port === '3000'
  )
  const paths = isBackend
    ? [cacheConfig.backendPath, cacheConfig.cdnPath]
    : [cacheConfig.cdnPath, cacheConfig.backendPath]

  return [...new Set(paths.filter(Boolean))].map((path) => joinUrl(endpoint, path))
}

async function fetchJsonWithFallback(urls, signal) {
  let lastError = null
  for (const url of urls) {
    try {
      return await fetchJson(url, signal)
    } catch (error) {
      if (error?.name === 'AbortError' || signal?.aborted) throw error
      lastError = error
    }
  }
  throw lastError || new Error('No cache endpoint is configured')
}

function normalizeTopics(topics, allowedTopics) {
  const requested = Array.isArray(topics) ? topics : allowedTopics
  return [...new Set(requested.filter((topic) => allowedTopics.includes(topic)))].sort()
}

export function GlobalMemoryProvider({
  children,
  endpoint = process.env.NEXT_PUBLIC_GLOBAL_MEMORY_URL
    || process.env.NEXT_PUBLIC_BACKEND_API
    || 'https://backend.hazcams.com',
  socketPath = process.env.NEXT_PUBLIC_GLOBAL_MEMORY_SOCKET_PATH || '/cdn.socket',
  stationWeatherSeconds = 86400,
  subscriptionData = null,
}) {
  const storeRef = useRef(null)
  if (!storeRef.current) storeRef.current = createGlobalMemoryStore()

  const runtimeRef = useRef({
    socket: null,
    globalTopicRefs: new Map(),
    stationTopicRefs: new Map(),
    presenceRefs: new Map(),
    blockedScopes: new Set(),
    eventQueues: new Map(),
    hydrationGeneration: new Map(),
    cachePromises: new Map(),
    abortController: null,
  })
  const subscriptionDataRef = useRef(subscriptionData)
  subscriptionDataRef.current = subscriptionData

  const getEventScope = useCallback((eventName, payload) => (
    eventName.startsWith('station:')
      ? `station:${payload?.stationId}:${eventName}`
      : `global:${eventName}`
  ), [])

  const queueOrApply = useCallback((eventName, payload) => {
    const runtime = runtimeRef.current
    const scope = getEventScope(eventName, payload)
    if (runtime.blockedScopes.has(scope)) {
      const queue = runtime.eventQueues.get(scope) || []
      queue.push({ eventName, payload })
      runtime.eventQueues.set(scope, queue)
      return
    }
    storeRef.current.applyEvent(eventName, payload)
  }, [getEventScope])

  const finishHydration = useCallback((scope, generation) => {
    const runtime = runtimeRef.current
    if (runtime.hydrationGeneration.get(scope) !== generation) return
    runtime.blockedScopes.delete(scope)
    const queue = runtime.eventQueues.get(scope) || []
    runtime.eventQueues.delete(scope)
    queue.forEach(({ eventName, payload }) => storeRef.current.applyEvent(eventName, payload))
  }, [])

  const beginHydration = useCallback((scope) => {
    const runtime = runtimeRef.current
    const generation = (runtime.hydrationGeneration.get(scope) || 0) + 1
    runtime.hydrationGeneration.set(scope, generation)
    runtime.blockedScopes.add(scope)
    runtime.eventQueues.set(scope, [])
    return generation
  }, [])

  const getSharedCacheRequest = useCallback((key, urlOrUrls, signal) => {
    const runtime = runtimeRef.current
    if (runtime.cachePromises.has(key)) return runtime.cachePromises.get(key)
    const urls = Array.isArray(urlOrUrls) ? urlOrUrls : [urlOrUrls]
    const request = fetchJsonWithFallback(urls, signal)
      .finally(() => runtime.cachePromises.delete(key))
    runtime.cachePromises.set(key, request)
    return request
  }, [])

  const refreshGlobalTopic = useCallback(async (
    topic,
    signal = runtimeRef.current.abortController?.signal
  ) => {
    if (!GLOBAL_MEMORY_EVENTS.includes(topic)) return
    const scope = `global:${topic}`
    const generation = beginHydration(scope)
    const cacheConfig = GLOBAL_CACHE_BY_TOPIC[topic]
    storeRef.current.setGlobalTopicHydration(topic, { status: 'loading', error: null })

    if (!cacheConfig) {
      storeRef.current.setGlobalTopicHydration(topic, { status: 'ready', error: null, refreshedAt: new Date().toISOString() })
      finishHydration(scope, generation)
      return
    }

    try {
      const payload = await getSharedCacheRequest(
        scope,
        getGlobalCacheUrls(endpoint, cacheConfig),
        signal
      )
      if (signal?.aborted) return
      storeRef.current.hydrateGlobalDomain(cacheConfig.domain, payload)
      storeRef.current.setGlobalTopicHydration(topic, {
        status: 'ready',
        error: null,
        refreshedAt: new Date().toISOString(),
      })
    } catch (error) {
      if (error?.name === 'AbortError' || signal?.aborted) return
      storeRef.current.setGlobalTopicHydration(topic, {
        status: 'degraded',
        error: error?.message || 'Unknown cache error',
        refreshedAt: new Date().toISOString(),
      })
    }
    finishHydration(scope, generation)
  }, [beginHydration, endpoint, finishHydration, getSharedCacheRequest])

  const refreshStationTopic = useCallback(async (
    stationId,
    topic,
    signal = runtimeRef.current.abortController?.signal
  ) => {
    if (!stationId || !STATION_MEMORY_EVENTS.includes(topic)) return
    const id = String(stationId)
    const scope = `station:${id}:${topic}`
    const generation = beginHydration(scope)
    const cacheKind = STATION_CACHE_BY_TOPIC[topic]
    storeRef.current.setStationHydration(id, { status: 'loading', error: null })

    if (!cacheKind) {
      storeRef.current.setStationHydration(id, { status: 'ready', error: null, refreshedAt: new Date().toISOString() })
      finishHydration(scope, generation)
      return
    }

    const encodedId = encodeURIComponent(id)
    const urls = cacheKind === 'weather'
      ? getGlobalCacheUrls(endpoint, {
          cdnPath: `/api/weather/${encodedId}/weather/${stationWeatherSeconds}.json`,
          backendPath: `/external/stations/${encodedId}/weather.json?seconds=${stationWeatherSeconds}&decimals=true`,
        })
      : [joinUrl(endpoint, `/api/station/${encodedId}.json`)]

    try {
      const payload = await getSharedCacheRequest(`station:${id}:${cacheKind}`, urls, signal)
      if (signal?.aborted) return
      storeRef.current.hydrateStation(id, {
        metadata: cacheKind === 'metadata' ? payload : null,
        weather: cacheKind === 'weather' ? payload : null,
      })
      storeRef.current.setStationHydration(id, {
        status: 'ready',
        error: null,
        refreshedAt: new Date().toISOString(),
      })
    } catch (error) {
      if (error?.name === 'AbortError' || signal?.aborted) return
      storeRef.current.setStationHydration(id, {
        status: 'degraded',
        error: error?.message || 'Unknown cache error',
        refreshedAt: new Date().toISOString(),
      })
    }
    finishHydration(scope, generation)
  }, [beginHydration, endpoint, finishHydration, getSharedCacheRequest, stationWeatherSeconds])

  const startGlobalTopic = useCallback((topic) => {
    const socket = runtimeRef.current.socket
    if (!socket?.connected) return
    refreshGlobalTopic(topic)
    socket.emit('subscribe', { global: true, topics: [topic] })
  }, [refreshGlobalTopic])

  const startStationTopic = useCallback((stationId, topic) => {
    const runtime = runtimeRef.current
    const socket = runtime.socket
    if (!socket?.connected) return
    const id = String(stationId)
    refreshStationTopic(id, topic)
    socket.emit('subscribe', {
      stationId: id,
      topics: [topic],
      ...(topic === 'station:presence'
        && (runtime.presenceRefs.get(id) || 0) > 0
        && subscriptionDataRef.current
        ? { user: subscriptionDataRef.current }
        : {}),
    })
  }, [refreshStationTopic])

  const acquireGlobalTopic = useCallback((topic) => {
    if (!GLOBAL_MEMORY_EVENTS.includes(topic)) return () => {}
    const refs = runtimeRef.current.globalTopicRefs
    const previousCount = refs.get(topic) || 0
    refs.set(topic, previousCount + 1)
    if (previousCount === 0) startGlobalTopic(topic)

    let released = false
    return () => {
      if (released) return
      released = true
      const nextCount = Math.max(0, (refs.get(topic) || 1) - 1)
      if (nextCount) refs.set(topic, nextCount)
      else {
        refs.delete(topic)
        runtimeRef.current.socket?.emit('unsubscribe', { global: true, topics: [topic] })
      }
    }
  }, [startGlobalTopic])

  const acquireStation = useCallback((stationId, { topics, trackPresence = false } = {}) => {
    if (!stationId) return () => {}
    const runtime = runtimeRef.current
    const id = String(stationId)
    const requestedTopics = normalizeTopics(topics, STATION_MEMORY_EVENTS)
    const topicRefs = runtime.stationTopicRefs.get(id) || new Map()
    runtime.stationTopicRefs.set(id, topicRefs)

    const previousPresenceCount = runtime.presenceRefs.get(id) || 0
    if (trackPresence) runtime.presenceRefs.set(id, previousPresenceCount + 1)

    requestedTopics.forEach((topic) => {
      const previousCount = topicRefs.get(topic) || 0
      topicRefs.set(topic, previousCount + 1)
      if (previousCount === 0) startStationTopic(id, topic)
    })

    let released = false
    return () => {
      if (released) return
      released = true
      requestedTopics.forEach((topic) => {
        const nextCount = Math.max(0, (topicRefs.get(topic) || 1) - 1)
        if (nextCount) topicRefs.set(topic, nextCount)
        else {
          topicRefs.delete(topic)
          runtime.socket?.emit('unsubscribe', { stationId: id, topics: [topic] })
        }
      })
      if (topicRefs.size === 0) runtime.stationTopicRefs.delete(id)

      if (trackPresence) {
        const nextPresenceCount = Math.max(0, (runtime.presenceRefs.get(id) || 1) - 1)
        if (nextPresenceCount) runtime.presenceRefs.set(id, nextPresenceCount)
        else runtime.presenceRefs.delete(id)
      }
    }
  }, [startStationTopic])

  const refreshGlobal = useCallback(async () => {
    await Promise.all(
      [...runtimeRef.current.globalTopicRefs.keys()].map((topic) => refreshGlobalTopic(topic))
    )
  }, [refreshGlobalTopic])

  const refreshStation = useCallback(async (stationId) => {
    const id = String(stationId)
    const topics = [...(runtimeRef.current.stationTopicRefs.get(id)?.keys() || [])]
    await Promise.all(topics.map((topic) => refreshStationTopic(id, topic)))
  }, [refreshStationTopic])

  const reconnect = useCallback(() => runtimeRef.current.socket?.connect(), [])
  const disconnect = useCallback(() => runtimeRef.current.socket?.disconnect(), [])

  useEffect(() => {
    const runtime = runtimeRef.current
    const abortController = new AbortController()
    runtime.abortController = abortController
    const socket = io(endpoint, {
      path: socketPath.startsWith('/') ? socketPath : `/${socketPath}`,
      // A single persistent transport works across horizontally scaled
      // backends without load-balancer session affinity.
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    })
    runtime.socket = socket
    storeRef.current.setConnection({ status: 'connecting', connected: false, error: null })

    const onConnect = () => {
      storeRef.current.setConnection({
        status: 'connected',
        connected: true,
        socketId: socket.id,
        error: null,
      })
      runtime.globalTopicRefs.forEach((_count, topic) => startGlobalTopic(topic))
      runtime.stationTopicRefs.forEach((topics, stationId) => {
        topics.forEach((_count, topic) => startStationTopic(stationId, topic))
      })
    }

    socket.on('connect', onConnect)
    socket.on('disconnect', (reason) => {
      storeRef.current.setConnection({
        status: socket.active ? 'reconnecting' : 'disconnected',
        connected: false,
        socketId: null,
        error: reason || null,
      })
    })
    socket.on('connect_error', (error) => {
      storeRef.current.setConnection({
        status: 'error',
        connected: false,
        error: error?.message || 'Socket.IO connection failed',
      })
    })
    socket.on('connected', (payload) => {
      storeRef.current.setConnection({ upstreamConnected: payload?.upstreamConnected ?? null })
    })
    socket.on('subscribe:error', (payload) => {
      storeRef.current.setConnection({ error: payload?.message || 'Subscription failed' })
    })
    GLOBAL_MEMORY_EVENTS.forEach((eventName) => {
      socket.on(eventName, (payload) => queueOrApply(eventName, payload))
    })
    STATION_MEMORY_EVENTS.forEach((eventName) => {
      socket.on(eventName, (payload) => queueOrApply(eventName, payload))
    })

    const heartbeat = setInterval(() => {
      if (socket.connected) socket.emit('heartbeat', {})
    }, 15000)

    return () => {
      clearInterval(heartbeat)
      abortController.abort()
      socket.removeAllListeners()
      socket.disconnect()
      runtime.socket = null
      runtime.blockedScopes.clear()
      runtime.eventQueues.clear()
      runtime.cachePromises.clear()
      storeRef.current.setConnection({ status: 'disconnected', connected: false, socketId: null })
    }
  }, [endpoint, queueOrApply, socketPath, startGlobalTopic, startStationTopic])

  const value = useMemo(() => ({
    store: storeRef.current,
    acquireGlobalTopic,
    acquireStation,
    refreshGlobal,
    refreshStation,
    reconnect,
    disconnect,
  }), [acquireGlobalTopic, acquireStation, disconnect, reconnect, refreshGlobal, refreshStation])

  return <GlobalMemoryContext.Provider value={value}>{children}</GlobalMemoryContext.Provider>
}

function useGlobalMemoryContext() {
  const context = useContext(GlobalMemoryContext)
  if (!context) throw new Error('Global memory hooks must be used within GlobalMemoryProvider')
  return context
}

export function useGlobalMemory(selector = (state) => state) {
  const { store } = useGlobalMemoryContext()
  const selectorRef = useRef(selector)
  const selectionRef = useRef({ state: null, selector: null, value: undefined })
  selectorRef.current = selector

  const getSnapshot = useCallback(() => {
    const state = store.getState()
    const currentSelector = selectorRef.current
    const cached = selectionRef.current
    if (cached.state !== state || cached.selector !== currentSelector) {
      selectionRef.current = { state, selector: currentSelector, value: currentSelector(state) }
    }
    return selectionRef.current.value
  }, [store])

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot)
}

export function useGlobalMemoryTopic(topic, selector, { enabled = true } = {}) {
  const { acquireGlobalTopic } = useGlobalMemoryContext()
  const value = useGlobalMemory(selector)

  useEffect(() => {
    if (!enabled || !topic) return undefined
    return acquireGlobalTopic(topic)
  }, [acquireGlobalTopic, enabled, topic])

  return value
}

export function useGlobalMemoryActions() {
  const context = useGlobalMemoryContext()
  return useMemo(() => ({
    refreshGlobal: context.refreshGlobal,
    refreshStation: context.refreshStation,
    reconnect: context.reconnect,
    disconnect: context.disconnect,
    setCatalog: context.store.setCatalog,
  }), [context])
}

export function useStationMemory(
  stationId,
  { enabled = true, trackPresence = false, topics } = {}
) {
  const { acquireStation } = useGlobalMemoryContext()
  const id = stationId ? String(stationId) : null
  const topicKey = normalizeTopics(topics, STATION_MEMORY_EVENTS).join('|')
  const requestedTopics = useMemo(() => topicKey ? topicKey.split('|') : [], [topicKey])
  const station = useGlobalMemory((state) => (
    id ? state.stations[id] || EMPTY_STATION_MEMORY : EMPTY_STATION_MEMORY
  ))

  useEffect(() => {
    if (!enabled || !id || requestedTopics.length === 0) return undefined
    return acquireStation(id, { topics: requestedTopics, trackPresence })
  }, [acquireStation, enabled, id, requestedTopics, trackPresence])

  return station
}
